import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  AdaptiveGuideContext,
  DRAWER_COLLAPSED_STORAGE_KEY,
  INITIAL_STATE,
  readDrawerCollapsedFromStorage,
  type ActiveSurface,
  type AdaptiveGuideContextValue,
  type AdaptiveGuideState,
} from './AdaptiveGuideContext';

// Split out of AdaptiveGuideContext.tsx (react-refresh/only-export-components)
// — same rationale as AuthProvider.tsx: `useAdaptiveGuide` is imported
// widely, this Provider has a handful of call sites. Behavior unchanged.

interface AdaptiveGuideProviderProps {
  children: ReactNode;
  /**
   * Optional: pre-set the surface so that children don't need to call
   * `report({ activeSurface: '...' })` themselves.
   */
  surface?: ActiveSurface;
}

export function AdaptiveGuideProvider({ children, surface }: AdaptiveGuideProviderProps) {
  const [state, setState] = useState<AdaptiveGuideState>({
    ...INITIAL_STATE,
    activeSurface: surface ?? null,
  });

  const [isDrawerCollapsed, setDrawerCollapsedState] = useState<boolean>(
    readDrawerCollapsedFromStorage
  );

  const report = useCallback((partial: Partial<AdaptiveGuideState>) => {
    setState((prev) => ({ ...prev, ...partial }));
  }, []);

  const setGuideOpen = useCallback((open: boolean) => {
    setState((prev) => ({ ...prev, isGuideOpen: open }));
  }, []);

  const setDrawerCollapsed = useCallback((collapsed: boolean) => {
    setDrawerCollapsedState(collapsed);
    try {
      localStorage.setItem(DRAWER_COLLAPSED_STORAGE_KEY, collapsed ? 'true' : 'false');
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }, []);

  const value = useMemo<AdaptiveGuideContextValue>(
    () => ({ ...state, report, setGuideOpen, isDrawerCollapsed, setDrawerCollapsed }),
    [state, report, setGuideOpen, isDrawerCollapsed, setDrawerCollapsed]
  );

  return (
    <AdaptiveGuideContext.Provider value={value}>
      {children}
    </AdaptiveGuideContext.Provider>
  );
}
